/** Owns the on/off state for live-trade desktop alerts and mounts the
 * global watcher once. Enabling requests OS notification permission AND
 * unlocks the AudioContext — both need a user gesture, which the bell
 * click provides. The choice is persisted in localStorage so it survives
 * reloads.
 *
 * The bell (in AppHeader) reads/toggles this via `useNotifications()`. */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { ALERTS_ENABLED_KEY, requestNotificationPermission, unlockAudio } from "@/lib/notify";
import { useTradeNotifications } from "@/hooks/useTradeNotifications";

const STORAGE_KEY = ALERTS_ENABLED_KEY;

export interface NotificationsContextValue {
  enabled: boolean;
  enabling: boolean;
  enable: () => Promise<void>;
  disable: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

function readStored(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "1";
}

function writeStored(on: boolean): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
}

export function NotificationsProvider({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement {
  const [enabled, setEnabled] = useState<boolean>(readStored);
  const [enabling, setEnabling] = useState(false);

  const enable = useCallback(async () => {
    setEnabling(true);
    try {
      unlockAudio(); // must run inside the click gesture
      const permission = await requestNotificationPermission();
      const granted = permission === "granted";
      setEnabled(granted);
      writeStored(granted);
    } finally {
      setEnabling(false);
    }
  }, []);

  const disable = useCallback(() => {
    setEnabled(false);
    writeStored(false);
  }, []);

  useTradeNotifications(enabled);

  const value = useMemo<NotificationsContextValue>(
    () => ({ enabled, enabling, enable, disable }),
    [enabled, enabling, enable, disable],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (ctx === null) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return ctx;
}
